
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CreditCard, Link } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import { supabase } from '@/integrations/supabase/client';
import { toast } from "@/components/ui/use-toast";

type Quote = Tables<'quotes'>;

interface PaymentLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  quote: Quote;
  onPaymentUpdate?: () => void;
}

const PaymentLinkModal: React.FC<PaymentLinkModalProps> = ({
  isOpen,
  onClose,
  quote,
  onPaymentUpdate
}) => {
  const [manualUrl, setManualUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleManualSave = async () => {
    if (!manualUrl.trim()) {
      toast({
        title: "Error",
        description: "Please enter a payment URL",
        variant: "destructive",
      });
      return;
    }

    // Basic URL validation
    try {
      new URL(manualUrl);
    } catch {
      toast({
        title: "Error",
        description: "Please enter a valid URL",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const { data, error } = await supabase.rpc('update_quote_payment_link', {
        p_quote_id: quote.id,
        p_payment_url: manualUrl,
        p_payment_session_id: `manual_${Date.now()}`
      });

      if (error) throw error;

      const result = data as { success?: boolean; error?: string };

      if (result?.success) {
        toast({
          title: "Payment Link Added",
          description: "Manual payment link has been saved successfully",
        });
        onPaymentUpdate?.();
        onClose();
      } else {
        throw new Error(result?.error || 'Failed to save payment link');
      }
    } catch (error) {
      console.error('Error saving manual payment link:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save payment link",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleGeneratePayment = async () => {
    if (!quote.quoted_amount) {
      toast({
        title: "Error",
        description: "Quote amount not found",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-payment-link', {
        body: {
          quoteId: quote.id,
          amount: quote.quoted_amount,
          currency: quote.currency || 'ZAR'
        }
      });

      if (error) throw error;

      const result = data as { success?: boolean; error?: string };

      if (result?.success) {
        toast({
          title: "Payment Link Generated",
          description: "Payment link has been created and sent to customer",
        });
        onPaymentUpdate?.();
        onClose();
      } else {
        throw new Error(result?.error || 'Failed to generate payment link');
      }
    } catch (error) {
      console.error('Error generating payment link:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate payment link",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    setManualUrl('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Payment Link</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Manual URL Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Link className="w-4 h-4" />
              <Label className="text-sm font-medium">Manual Payment URL</Label>
            </div>
            <div className="space-y-2">
              <Input
                placeholder="https://your-payment-provider.com/pay/..."
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                disabled={isSaving || isGenerating}
              />
              <Button
                onClick={handleManualSave}
                disabled={!manualUrl.trim() || isSaving || isGenerating}
                className="w-full"
                variant="outline"
              >
                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Manual Link
              </Button>
            </div>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          {/* Auto Generate Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              <Label className="text-sm font-medium">Auto-Generate Payment Link</Label>
            </div>
            <Button
              onClick={handleGeneratePayment}
              disabled={!quote.quoted_amount || isGenerating || isSaving}
              className="w-full"
            >
              {isGenerating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Generate Payment Link
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentLinkModal;
